import { SessionTypes, SessionEvents, ParticipantTypes } from "./types"

export class Participant {
  #eventListeners = new Map()
  #customLocalMediaStream = null

  /**
   * 
   * @param {string} peerId - the id of the peer
   * @param {RTCPeerConnection} rtcPeerConnection - the RTCPeerConnection object
   * @param {ConnectionContext} context - the connection context
   * @param {any} dataChannelMessageListener - a callback for data channel messages. if not set, defaults to peer-to-peer listener
   */
  constructor({ peerId, rtcPeerConnection, context, participantType, dataChannelMessageListener, displayName }) {
    this.peerId = peerId
    this.rtcPeerConnection = rtcPeerConnection  
    this.context = context
    this.participantType = participantType
    this.displayName = displayName
    this.mediaStream = null
    this.dataChannel = null
    this.dataChannelMessageListener = dataChannelMessageListener || this.onDataChannelMessage.bind(this)
    this.localIceCandidates = []

    if (!this.peerId) {
      throw new Error("peerId is required")
    }
    if (!this.rtcPeerConnection) {
      throw new Error("rtcPeerConnection is required")
    }
    if (!this.context) {
      throw new Error("context is required")
    }
    if (!this.participantType) {
      throw new Error("participantType is required")
    }
  }

  // for backwards compatibility with UI
  get participantId() {
    // console.warn(`participantId is deprecated. use peerId instead: ${this.peerId}`);
    return String(this.peerId);
  }

  /**
   * Mutes this participant's incoming audio (outgoing audio is uneffected)
   */
  muteAudio() {
    this.setAudioEnabled(false);
  }

  /**
   * Unmutes this participant's incoming audio (outgoing audio is uneffected)
   */
  unmuteAudio() {
    this.setAudioEnabled(true);
  }

  setAudioEnabled(isEnabled) {
    if (this.mediaStream) {
      this.mediaStream.getAudioTracks().forEach((trk) => {
        trk.enabled = isEnabled;
      });
    }
  }

  get isPatient() {
    return this.participantType === ParticipantTypes.device;
  }

  /**
   * Adds an event callback
   * @param {string} eventType The type of event
   * @param {string} eventCallback A callback
   */
  on(eventType, eventCallback) {
    this.#eventListeners.set(eventType, eventCallback);
  }

  emit(eventType, ...eventArgs) {
    const callback = this.#eventListeners.get(eventType);
    if (callback) {
      // unwrap varargs and call callback
      callback.apply(null, eventArgs);
    }
  }

  addLocalMediaTrack(track, stream) {
    this.rtcPeerConnection.addTrack(track, stream);
    console.debug(`added local track ${track.kind} to peer ${this.peerId}`, track);
  }

  async connectLocalMedia() {
    const localMediaStream = this.#customLocalMediaStream ? this.#customLocalMediaStream : this.context.localMediaStream;
    if (localMediaStream) {
      console.debug(`connecting local media. state: ${this.rtcPeerConnection.connectionState}/ ${this.rtcPeerConnection.signalingState}`);
      localMediaStream.getTracks().forEach((track) => {
        this.addLocalMediaTrack(track, localMediaStream);
      });
    } else if (this.context.sessionType !== SessionTypes.test) {
      console.error("no local media stream available");
      this.emit("error", "no-local-media-stream");
    }
  }

  enableLocalIceCandidateCollection() {
    this.rtcPeerConnection.addEventListener('icecandidate', (event) => {
      if (event.candidate) {
        this.localIceCandidates.push(event.candidate);
        console.debug(`added local ice candidate to ${this.peerId} connection`, event.candidate);
      } else {
        console.debug(`ice candidate collection complete for ${this.peerId}`);
      }
    })
  }

  async collectLocalIceCandidates(maxWaitTimeMs = 1500) {
    return new Promise((resolve) => {
      const checkInterval = 100; // Check every 100ms
      const startTime = Date.now();
      const maxTime = startTime + maxWaitTimeMs;

      const checkIceGathering = () => {
        if (this.rtcPeerConnection.iceGatheringState === 'complete') {
          console.debug(`ice candidate collection complete for ${this.peerId}`);
          resolve(this.localIceCandidates);
          return;
        }

        if (Date.now() >= maxTime) {
          console.debug(`ice candidate collection timed out for ${this.peerId}`);
          resolve(this.localIceCandidates);
          return;
        }

        setTimeout(checkIceGathering, checkInterval);
      };

      checkIceGathering();
    });
  }

  async onDataChannelMessage(payload) {
    switch (payload.type) {
      case "get-display-info": {
        // a browser peer is requesting this user's display name
        const displayName = this.context.options.userDisplayName;
        this.dataChannel.send(JSON.stringify({
          "type": "display-info-answer",
          displayName,
        }));
        console.debug(`sent get-display-info (${displayName}) to ${this.peerId}`);
        break;
      }
      case "user-bye":
        // assuming the other peer disconnection logic can handle this
        console.info(`received user-bye from ${payload.peer_id}`);
        break;
      default:
        console.error(`unexpected data channel payload type: ${payload.type}`);
    }
  }

  configurePeerConnectionListeners() {
    const peerConn = this.rtcPeerConnection;
    const self = this;
    peerConn.addEventListener('track', (event) => {
      console.debug(`remote track event for ${this.peerId} ${typeof (this)}`, event, this);
      // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/track_event
      this.mediaStream = event.streams[0];
      this.emit("session-event", SessionEvents.participantTrackAdded, self);
    })

    peerConn.addEventListener('signalingstatechange', () => {
      console.debug(`${this.peerId} signaling state changed to ${peerConn.signalingState}`);
    })
    peerConn.addEventListener('icecandidateerror', (errEvent) => {
      console.warn(`ICE CANDIDATE ERROR: (${errEvent.errorCode}) ${errEvent.errorText}`, errEvent);
    });
    peerConn.addEventListener('iceconnectionstatechange', () => {
      console.debug(`${this.peerId} ice connect state changed to ${peerConn.iceConnectionState}`);
    });
    peerConn.addEventListener('icegatheringstatechange', () => {
      console.debug(`${this.peerId} ice gather state changed to ${peerConn.iceGatheringState}`);
    });
    peerConn.addEventListener('negotiationneeded', () => {
      console.warn(`unexpected renegotiation is needed for ${this.peerId}`);
    });

    peerConn.addEventListener('connectionstatechange', () => {
      console.debug(`${this.peerId} connection state changed to ${peerConn.connectionState}`);
      if (peerConn.connectionState === 'disconnected' || peerConn.connectionState === 'failed') {
        this.emit("disconnect", this);
      }
    })

    // for data channel to the video device, the channel is used to coordinate with other peers in a meeting.
    // data channels for peer-to-peer connections may also be setup for direct communication.
    // for example, the kiosk viewer uses a data channel to ask for the display name of the user
    peerConn.addEventListener("datachannel", (event) => {
      console.debug("data channel added event", event, this);
      this.dataChannel = event.channel;
      // add channel event listeners
      this.dataChannel.addEventListener("message", async (event) => {
        console.debug("data channel message", event, this);
        const payload = JSON.parse(event.data);
        await this.dataChannelMessageListener(payload);
      })
    })
  }

  async configureRemote(remoteSdp, remoteIceCandidates, customLocalMediaStream = null) {
    this.#customLocalMediaStream = customLocalMediaStream;
    const peerConn = this.rtcPeerConnection;
    // let expectedRemoteTracks = 0;

    if (!remoteSdp) {
      throw new Error("no remote SDP provided");
    }
    console.debug(`configuring remote sdp for ${this.peerId}. state: ${peerConn.connectionState}/ ${peerConn.signalingState}`);
    this.configurePeerConnectionListeners();

    const remoteDesc = new RTCSessionDescription(remoteSdp);
    console.debug(`setting remote description of type ${remoteDesc.type} for ${this.peerId}`);

    await peerConn.setRemoteDescription(remoteDesc);

    if (remoteIceCandidates) {
      remoteIceCandidates.forEach((cand) => {
        this.rtcPeerConnection.addIceCandidate(cand);
        console.debug(`added ice candidate to device ${this.peerId} connection`, cand);
      });
    }

    console.debug("connecting local media...")
    await this.connectLocalMedia();
  }

  async createBrowserPeerOffer() {
    console.debug(`creating offer for ${this.peerId}`);
    // NOTE: assume caller is in charge of ice collection
    this.configurePeerConnectionListeners();
    this.dataChannel = this.rtcPeerConnection.createDataChannel("browser-peer")
    await this.connectLocalMedia();
    const offer = await this.rtcPeerConnection.createOffer()
    return offer;
  }

  /**
   * Disconnects the participant's resources
   */
  disconnect() {
    console.debug(`disconnecting participant ${this.peerId}`);
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      try {
        this.dataChannel.send(JSON.stringify({
          peer_id: this.peerId,  // send the peer id of the participant that is disconnecting
          type: 'user-bye'
        }));
        console.debug(`sent user-bye to ${this.peerId}`);
      } catch (err) {
        console.warn(`error sending user-bye to ${this.peerId}`, err);
      }

      // close after a slight delay to make sure message goes through  
      setTimeout(() => {
        this.dataChannel.close();
        console.debug("data channel closed");
      }, 100);
    } else {
      console.debug(`data channel not open for ${this.peerId}`);
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((trk) => {
        trk.stop();
        console.debug(`stopped remote ${trk.kind} track (${trk.id}) for ${this.peerId}`, trk);
      });
    } else if (this.peerId !== 'kiosk-viewer') {  // kiosk-viewer is a special participant that doesn't send a media stream
      console.warn(`participant ${this.peerId} had no media stream while disconnecting`);
    }
    if (this.#customLocalMediaStream) {
      console.log(`disconnecting custom local stream for ${this.peerId}`, this.#customLocalMediaStream);
      this.#customLocalMediaStream.getTracks().forEach((track) => {
        track.stop();
        console.debug(`stopped custom local stream ${track.kind} track (${track.id})`, track);
      });
      this.#customLocalMediaStream = null;
    } else {
      console.debug(`participant ${this.peerId} had no local stream while disconnecting`);
    }
    if (this.rtcPeerConnection) {
      this.rtcPeerConnection.close();
      console.debug(`closed peer connection to ${this.peerId}`);
    } else {
      console.warn(`participant ${this.peerId} had no peer connection while disconnecting`);
    }
    this.mediaStream = null;
    this.rtcPeerConnection = null;
    this.#eventListeners.clear();
    this.localIceCandidates = [];
  }
}